/* Use the same dry product photography in every color scheme. */
(() => {
  const config = document.getElementById('grit-color-product-images');
  if (!config) return;

  const replacements = JSON.parse(config.textContent);
  const attributes = ['src', 'srcset', 'data-max-resolution'];
  const originalAttribute = 'data-grit-original-photo';
  const states = new WeakMap();
  const pending = new Set();
  let scheduled = false;

  function replaceUrl(value) {
    if (!value) return value;
    try {
      const original = new URL(value, document.baseURI);
      const filename = decodeURIComponent(original.pathname.split('/').pop());
      if (!replacements[filename]) return value;
      const dry = new URL(replacements[filename], document.baseURI);
      for (const name of ['width', 'height', 'crop', 'format']) {
        if (original.searchParams.has(name)) dry.searchParams.set(name, original.searchParams.get(name));
      }
      return dry.href;
    } catch {
      return value;
    }
  }

  function replaceCandidates(value) {
    if (value == null) return value;
    return value.split(',').map((candidate) => candidate.trim().replace(/^\S+/, replaceUrl)).join(', ');
  }

  function read(image) {
    return Object.fromEntries(attributes.map((name) => [name, image.getAttribute(name)]));
  }

  function apply(image, state) {
    const useDry = !state.failed;
    const desired = Object.fromEntries(attributes.map((name) => [
      name,
      useDry ? (name === 'srcset' ? replaceCandidates(state.original[name]) : replaceUrl(state.original[name])) : state.original[name],
    ]));
    state.last = desired;
    for (const name of attributes) {
      if (image.getAttribute(name) === desired[name]) continue;
      if (desired[name] == null) image.removeAttribute(name);
      else image.setAttribute(name, desired[name]);
    }
  }

  function update(image) {
    if (!image.isConnected) return;
    const current = read(image);
    let state = states.get(image);
    if (!state) {
      let saved;
      try { saved = JSON.parse(image.getAttribute(originalAttribute)); } catch { /* Ordinary new image. */ }
      state = { original: saved || current, last: current, failed: false };
    } else {
      for (const name of attributes) {
        if (current[name] !== state.last[name]) {
          state.original[name] = current[name];
          state.failed = false;
        }
      }
    }

    const known = attributes.some((name) => {
      const value = state.original[name];
      if (!value) return false;
      return (name === 'srcset' ? value.split(',').map((candidate) => candidate.trim().split(/\s+/)[0]) : [value])
        .some((url) => replaceUrl(url) !== url);
    });
    if (!known) return;
    if (!states.has(image)) {
      states.set(image, state);
      image.addEventListener('error', () => {
        if (state.failed) return;
        state.failed = true;
        apply(image, state);
      });
    }
    // Native gallery zoom clones this attribute along with the image, so it
    // retains the original source for the image-error fallback.
    image.setAttribute(originalAttribute, JSON.stringify(state.original));
    apply(image, state);
  }

  function queue(image) {
    pending.add(image);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const images = [...pending];
      pending.clear();
      images.forEach(update);
    });
  }

  function scan(root) {
    if (root.matches?.('img')) queue(root);
    root.querySelectorAll?.('img').forEach(queue);
  }

  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') queue(record.target);
      else record.addedNodes.forEach(scan);
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: attributes });

  scan(document);
})();
